import { CanonicalRepresentationRequestInputData } from "src/model/CanonicalRepresentationRequestInputData";
import React, {useEffect, useState} from "react";
import { useStyles } from "src/styles/SearchFormStyles";
import {
    HARDWARE_SOURCE_TO_DEFAULT_AFFINITY_THRESHOLD_MAP,
    ITEM_ID_TYPE_DROPDOWN, 
    MAX_RESULTS_HELP_MESSAGE,
    REGION_DROPDOWN,
    REGION_HARDWARE_SOURCE_MAP
} from "../CIRepSearchForm/constant";
import { inputValidation } from "src/utils/inputValidation";
import { Button, FormControl, Grid, Typography } from "@material-ui/core";
import Container from "../../components/common/Container";
import FormSelect from "../../components/common/FormSelect";
import FormTextInput from "../../components/common/FormTextInput";
import HardwareSourceAndAffinityThresholdSelects
    from "../CIRepSearchForm/HardwareSourceAndAffinityThresholdSelects";


interface CIRepSearchFormProps {
    initialInputData?: CanonicalRepresentationRequestInputData;
    onSubmit: (value: CanonicalRepresentationRequestInputData ) => void
}

/**
 * The Component is used to provide the user a form, allows users to select/input Item Identifier Type,
 * Item Identifier, Hardware Source, etc. and search Canonical Representation.
 * It will check if users input a valid Item Identifier, Max Results and provide all required data.
 */
const CIRepSearchForm: React.FunctionComponent<CIRepSearchFormProps> = (props: CIRepSearchFormProps) => {
    const styles = useStyles();
    const [region, setRegion] = useState('');
    const [hardwareSource, setHardwareSource] = useState('');
    const [itemIdType, setItemIdType] = useState('');
    const [itemId, setItemId] = useState('');
    const [affinityThreshold, setAffinityThreshold] = useState('ALL');
    const [maxResults, setMaxResults] = useState('');

    const [errorMessage, setErrorMessage] = useState('');
    const [showRegionError, setShowRegionError] = useState(false);
    const [showHardwareSourceError, setShowHardwareSourceError] = useState(false);
    const [showItemIdTypeError, setShowItemIdTypeError] = useState(false);
    const [showItemIdError, setShowItemIdError] = useState(false);
    const [showMaxResultsError, setShowMaxResultsError] = useState(false);

    useEffect(() => {
        if (props.initialInputData) {
            setRegion(props.initialInputData.region);
            setHardwareSource(props.initialInputData.hardwareSource);
            if (props.initialInputData.affinityThreshold !== 'ALL') {
                setAffinityThreshold(props.initialInputData.affinityThreshold);
            }
            setItemIdType(props.initialInputData.itemIdType);
            setItemId(props.initialInputData.itemId);
            if (props.initialInputData.maxResults) {
                setMaxResults(props.initialInputData.maxResults.toString());
            }
        }
    },[props.initialInputData]);

    //check item id validation
    const checkIsInputItemIdValid = (itemId: string) => {
        return itemId ? inputValidation.isItemIdValid(itemId) : true;
    }

    //check max results validation
    const checkIsInputMaxResultsValid = (maxResults: string, hardwareSource: string) => {
        return maxResults ? inputValidation.isMaxResultsValid(hardwareSource, maxResults) : true;
    }

    const handleRegionSelect = (value: string) => {
        setRegion(value);
        //when change region, reset hardware source and affinity threshold
        setHardwareSource('');
        setAffinityThreshold('ALL');
        setShowRegionError(false);
    }

    const handleHardwareSourceSelect = (value: string) => {
        setHardwareSource(value);
        if (value === "ALL") {
            setAffinityThreshold("ALL");
            if (maxResults !== "") {
                setShowMaxResultsError(true);
            }
        } else if (maxResults !== "") {
            setAffinityThreshold("ALL");
        } else {
            const hardwareSource = value as keyof typeof HARDWARE_SOURCE_TO_DEFAULT_AFFINITY_THRESHOLD_MAP;
            setAffinityThreshold(HARDWARE_SOURCE_TO_DEFAULT_AFFINITY_THRESHOLD_MAP[hardwareSource]);
        }
        if (checkIsInputMaxResultsValid(maxResults, value)) {
            setShowMaxResultsError(false);
        }
        setShowHardwareSourceError(false);
    }

    const handleAffinityThresholdSelect = (value: string) => {
        setAffinityThreshold(value);
    }

    const handleItemIdTypeSelect = (value: string) => {
        setItemIdType(value);
        setShowItemIdTypeError(false);
    }

    const handleItemIdInput = (value: string) => {
        setItemId(value.trim());
        setShowItemIdError(false);
    }

    const handleMaxResultsInput = (value: string) => {
        setMaxResults(value.trim());
        setAffinityThreshold("ALL");
        setShowMaxResultsError(false);
    }

    let relatedHardwareSource: { name: string; value: string; }[] =[];
    if (region !== "") {
        const regionTyped = region as keyof typeof REGION_HARDWARE_SOURCE_MAP;
        relatedHardwareSource = REGION_HARDWARE_SOURCE_MAP[regionTyped];
    }

    const onSubmitClick =() => {
        //check if all required fields are provided
        if (region === "" || hardwareSource === "" || itemIdType === "" || itemId === "") {
            setErrorMessage("Region, Hardware Source, Item Identifier Type, and Item Identifier are required fields!");

            if(region === "") {
                setShowRegionError(true);
            }

            if(hardwareSource === "") {
                setShowHardwareSourceError(true);
            }

            if(itemIdType === "") {
                setShowItemIdTypeError(true);
            }

            if(itemId === "") {
                setShowItemIdError(true);
            }
            return;
        }

        //check if input is valid
        if (!checkIsInputItemIdValid(itemId)) {
            setShowItemIdError(true);
            setErrorMessage("Item Identifier is not valid!");
            return;
        }

        if (!checkIsInputMaxResultsValid(maxResults, hardwareSource)) {
            setShowMaxResultsError(true);
            setErrorMessage("Max Results is not valid! " +
                "Max Results should be positive integer and can be set when only one hardware source is chosen!")
            return;
        }

        let inputData = {
            itemId: itemId,
            itemIdType: itemIdType,
            affinityThreshold: affinityThreshold,
            hardwareSource: hardwareSource,
            maxResults: parseInt(maxResults),
            region: region
        }
        props.onSubmit(inputData);

        setShowMaxResultsError(false);
        setErrorMessage("");
    }

    let errorField =  (
        <Grid
            className={styles.errorGrid}
            container
            alignItems="center">
            <Typography
                component='h4'
                className={(errorMessage === "" ? "" : `${styles.error}`)}>
                {errorMessage}
            </Typography>
        </Grid>
    );

    return (
        <>
            <Container  paperProps={{ elevation: 2 }} boxProps={{ p: 1}}>
                <div className={styles.formGrid}>
                    <Grid item >
                        <FormControl fullWidth>
                            <FormSelect
                                selectedValue={region}
                                label="Region:"
                                placeholder="Please select region"
                                showError={showRegionError}
                                options={REGION_DROPDOWN}
                                onSelect={handleRegionSelect}/>
                        </FormControl>
                    </Grid>
                    <HardwareSourceAndAffinityThresholdSelects
                        hardwareSource={hardwareSource}
                        affinityThreshold={affinityThreshold}
                        hardwareSourceOptions={relatedHardwareSource}
                        showHardwareSourceError={showHardwareSourceError}
                        onSelectHardwareSource={handleHardwareSourceSelect}
                        onSelectAffinityThreshold={handleAffinityThresholdSelect} />

                    <Grid item >
                        <FormControl fullWidth>
                            <FormSelect
                                selectedValue={itemIdType}
                                showError={showItemIdTypeError}
                                label="Item Identifier Type:"
                                placeholder="Please select item Id Type"
                                options={ITEM_ID_TYPE_DROPDOWN}
                                onSelect={handleItemIdTypeSelect}/>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl fullWidth>
                            <FormTextInput
                                inputValue={itemId}
                                label="Item Identifier:"
                                placeholder="Please input Item Id"
                                showError={showItemIdError}
                                onInput={handleItemIdInput}
                                showHelp={false}/>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl fullWidth>
                            <FormTextInput
                                inputValue={maxResults}
                                label="Max Results:"
                                placeholder="(Optional) Please input Max Results"
                                showError={showMaxResultsError}
                                onInput={handleMaxResultsInput}
                                showHelp={true}
                                helpMessage={MAX_RESULTS_HELP_MESSAGE}/>
                        </FormControl>
                    </Grid>
                </div>
                <Grid item >
                    <FormControl fullWidth>
                        <Button
                            className={styles.searchButton}
                            variant="contained"
                            onClick={onSubmitClick}>
                            Search
                        </Button>
                    </FormControl>
                </Grid>
            </Container>
            {errorField}
        </>
    );
}

export default CIRepSearchForm;